import React, {useState} from 'react';
import {z} from 'zod';
import {Link, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {CheckIcon} from '@heroicons/react/24/outline';
import {queryClient} from 'App';
import {getGetProjectsQueryKey, useCreateProject, type CreateProjectBody, type ProjectType} from 'api/generated';
import {Button, InputText, Modal} from 'components';
import {useMe} from 'hooks';
import {isMobile} from 'react-device-detect';

interface IModalProjectCreateProps {
  visible: boolean;
  onClose: () => void;
}

const ModalProjectCreate = ({visible, onClose}: IModalProjectCreateProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [project, setProject] = useState<ProjectType>();
  const handleCreated = (project: ProjectType) => {
    setProject(project);
    setStep(2);
  };
  const handleClose = async () => {
    onClose();
    await new Promise(resolve => setTimeout(resolve, 300));
    setStep(1);
  };
  return (
    <Modal visible={visible} onClose={handleClose}>
      {step === 1 && <FormProjectCreate onClose={handleClose} onCreated={handleCreated} />}
      {step === 2 && <ContinueToProject project={project} />}
    </Modal>
  );
};

export default React.memo(ModalProjectCreate);

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Link is required'),
  recordAnalytics: z.boolean().optional(),
});

interface IFormProjectCreateProps {
  onCreated: (project: ProjectType) => void;
  onClose: () => void;
}

const FormProjectCreate = ({onCreated, onClose}: IFormProjectCreateProps) => {
  const me = useMe();
  const handle = me?.handle ?? '';
  const createProject = useCreateProject();
  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: zodResolver(schema),
  });
  const onSubmit = async (data: CreateProjectBody) => {
    const {data: res} = await createProject.mutateAsync({data});
    if (res.data) {
      toast.success('Project created');
      queryClient.invalidateQueries({queryKey: [getGetProjectsQueryKey({filter: 'all'})][0]});
      onCreated(res.data);
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };
  const linkPrefix = (() => {
    let prefix = `/ar/${handle}/`;
    if (!isMobile) prefix = 'app.das-ar.com' + prefix;
    return prefix;
  })();
  return (
    <div>
      <h2 className="text-2xl dark:text-white mb-5">New project</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputText label="Name" {...register('name')} error={errors.name?.message} />
        <InputText label="Link" {...register('slug')} prefix={linkPrefix} error={errors.slug?.message} />
        <div className="mt-5 flex items-center justify-between">
          <div className="w-20">
            <Button text="Cancel" fullWidth size="extra-small" variant="secondary" disabled={createProject.isPending} onClick={onClose} />
          </div>
          <div className="w-20">
            <Button type="submit" text="Save" fullWidth size="extra-small" loading={createProject.isPending} />
          </div>
        </div>
      </form>
    </div>
  );
};

interface IContinueToProjectProps {
  project: ProjectType | undefined;
}

const ContinueToProject = ({project}: IContinueToProjectProps) => {
  const params = useParams();
  const workspace = params.workspace;
  return (
    <div>
      <div>
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary">
          <CheckIcon aria-hidden="true" className="size-6 text-white" />
        </div>
        <div className="mt-3 text-center sm:mt-5">
          <h2 className="text-2xl dark:text-white mb-5">Project created</h2>
          <div className="mt-2">
            <p className="text-sm/6 text-gray-700 dark:text-darkText">
              Add and assign 3D models to it. Edit and save your progress. Finally, publish it and share the QR or link with your friends, customers,
              or team members.
            </p>
          </div>
        </div>
      </div>
      <div className="mt-8 flex justify-center">
        <Link to={`/${workspace}/projects/${project?.uid}`}>
          <Button text="Continue" fullWidth size="small" />
        </Link>
      </div>
    </div>
  );
};
